import { preloadExecutionDate } from "./consolidationPresentation.js";
import { breakComplete, breakReasons, sheetBreaks } from "./consolidationBreaks.js";

// Soirée de préchargement d'un employé, comme un relevé de jour : début et fin du service, dîner, pauses, départ et retour au
// dépôt de chaque camion ; chaque préchargement en est un arrêt, avec son « Quitté à » (sa fin).
export const eveningKey = (employeeId, executionDate) => `preload-service:${employeeId}:${executionDate}`;
export const emptyEvening = (employeeId, executionDate) => ({ id: null, employee_id: employeeId, execution_date: executionDate,
  service_start: null, service_end: null, no_lunch: true, lunch_start: null, lunch_end: null, breaks: [],
  trucks: {}, notes: null, status: "draft", version: 0 });

const truckOf = row => row.links[0]?.truck_id || "";
export const zero = (row, quantities = {}) => {
  const values = row.links.filter(link => !link.execution_excluded).map(link => quantities[link.order_id] ?? link.loaded_tons);
  return values.length > 0 && values.every(value => value !== "" && value != null && Number(value) === 0);
};

// Une soirée par employé (celui de la ligne, sinon l'employé par défaut) et par date d'exécution ; sans employé, « à choisir ».
// rows : préchargements tels qu'affichés (brouillons compris).
export function eveningGroups(rows, defaultEmployeeId = null) {
  const groups = new Map();
  for (const row of rows) {
    const employeeId = row.employee_id || (row.status === "draft" ? defaultEmployeeId : null) || null;
    const executionDate = preloadExecutionDate(row);
    const key = employeeId ? eveningKey(employeeId, executionDate) : `preload-service:none:${executionDate}`;
    if (!groups.has(key)) groups.set(key, { key, employeeId, executionDate, rows: [] });
    groups.get(key).rows.push(row);
  }
  return [...groups.values()].sort((a, b) => (a.employeeId ? 0 : 1) - (b.employeeId ? 0 : 1) || a.executionDate.localeCompare(b.executionDate));
}

// « 24, 25 et 26 octobre » : le mois une seule fois, après son dernier jour.
export function deliveryDaysLabel(days) {
  const month = day => new Intl.DateTimeFormat("fr-CA", { month: "long", timeZone: "UTC" }).format(new Date(`${day}T12:00:00Z`));
  const labels = days.map((day, index) => `${Number(day.slice(8))}${days[index + 1]?.slice(0, 7) === day.slice(0, 7) ? "" : ` ${month(day)}`}`);
  return labels.length > 1 ? `${labels.slice(0, -1).join(", ")} et ${labels.at(-1)}` : labels[0];
}

// Camions de la soirée dans l'ordre de leur départ (sinon de leur premier « Quitté à »), chacun avec ses préchargements.
export function eveningTrucks(rows, evening) {
  const trucks = new Map();
  for (const row of rows) {
    const id = truckOf(row);
    if (!trucks.has(id)) trucks.set(id, { id, number: row.links[0]?.truck_number || "", rows: [] });
    trucks.get(id).rows.push(row);
  }
  const first = truck => Date.parse(evening?.trucks?.[truck.id]?.departure || "") || Math.min(...truck.rows.map(row => Date.parse(row.end_at || "") || Infinity));
  return [...trucks.values()].map(truck => ({ ...truck, rows: [...truck.rows].sort((a, b) => (Date.parse(a.end_at || "") || Infinity) - (Date.parse(b.end_at || "") || Infinity)) }))
    .sort((a, b) => first(a) - first(b));
}

// Heures payées : fin − début − dîner − pauses non payées ; null tant que le service est incomplet.
// Les heures facturées retirent aussi la pause payée (evening_shares).
export function eveningWorkedSeconds(evening) {
  const span = (a, b) => (a && b ? (Date.parse(b) - Date.parse(a)) / 1000 : 0);
  const total = span(evening?.service_start, evening?.service_end);
  if (!(total > 0)) return null;
  return total - (evening.no_lunch ? 0 : span(evening.lunch_start, evening.lunch_end))
    - sheetBreaks(evening).filter(pause => breakComplete(pause) && pause.paid !== true).reduce((sum, pause) => sum + span(pause.start, pause.end), 0);
}

// Ce qui empêche de valider la soirée ; rows : préchargements en cours de saisie, avec leurs tonnages saisis.
export function eveningValidationMessage(evening, rows) {
  // Rien de fait ce soir-là (tous les préchargements à zéro tonne) : pas de service à saisir.
  if (rows.every(({ row, quantities }) => zero(row, quantities))) return null;
  if (!evening.service_start || !evening.service_end) return "Renseignez le début et la fin du service.";
  if (Date.parse(evening.service_end) <= Date.parse(evening.service_start)) return "La fin du service doit suivre son début.";
  if (!evening.no_lunch && Boolean(evening.lunch_start) !== Boolean(evening.lunch_end)) return "Complétez les deux heures du dîner.";
  if (breakReasons(evening).length) return breakReasons(evening)[0];
  for (const truck of eveningTrucks(rows.filter(({ row, quantities }) => !zero(row, quantities)).map(({ row }) => row), evening)) {
    const times = evening.trucks?.[truck.id] || {};
    if (!times.departure || !times.return_at) return `Camion ${truck.number} : renseignez le départ et le retour au dépôt.`;
  }
  return null;
}

// Ce qui manque à un préchargement de la soirée : ses tonnages, puis son employé et son « Quitté à » s'il a été fait.
export function eveningRowMessage(row, quantities = {}) {
  if (!row.links.length) return "Ajoutez au moins une commande.";
  const values = row.links.filter(link => !link.execution_excluded).map(link => quantities[link.order_id] ?? link.loaded_tons);
  if (values.some(value => value == null || String(value).trim() === "" || !Number.isFinite(Number(value)) || Number(value) < 0)) {
    return "Renseignez chaque tonnage chargé (0 si non effectué).";
  }
  if (zero(row, quantities)) return null;
  if (!row.employee_id) return "Choisissez l’employé.";
  return row.end_at ? null : "Renseignez le « Quitté à ».";
}
